/* eslint no-underscore-dangle: ["error", { "allow": ["__INITIAL_STATE__", "__STORE__"] }] */
import omit from 'lodash/omit';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getUrlParam } from 'Utils/Content';
import { isNonAnonymousUser, isAnonymousUser, getUserMvpdId, isMvpdUser, getUser } from 'Utils/UserCookie';
import { userInitialState } from 'State/User';
import { logger } from 'Utils/Logger';
import initialState from './initialState';

const isSSR = getUrlParam('isSSR', true);

const getDefaultState = (state) => {
  let defaultState = {};
  if (state) {
    const { plans, pageLayout, ...rest } = state;
    if (pageLayout && pageLayout.footerContent) {
      pageLayout.footerContent.isLoaded = true;
      defaultState.pageLayout = pageLayout;
    }
    // to always refetch plans on client
    if (plans) {
      plans.getPlansSuccess = false;
      plans.isFetchingPlans = false;
      defaultState.plans = plans;
    }

    defaultState = { ...rest, ...defaultState };
  }
  return defaultState;
};

export const getAppConfig = () => {
  const screens = GLOBAL_SCREENS;
  const routes = GLOBAL_ROUTES;
  const strings = GLOBAL_STRINGS;
  const defaultConfig = GLOBAL_CONFIG;
  const pageMeta = GLOBAL_PAGEMETA;

  return { screens, strings, routes, defaultConfig, pageMeta };
};

const getInitialUserState = async () => {
  try {
    const token = await getUser();
    const isAnonymous = await isAnonymousUser();
    const isAuth = token && (await isNonAnonymousUser());
    if (isAuth) {
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      logger.setAttr('profile_id', user.profileId);
      return {
        ...userInitialState,
        isAuthenticated: true,
        ...user,
      };
    }
    if (isAnonymous) {
      const user = omit(token, ['profileId', 'accessToken', 'tokenExpiration', 'accessTokenExpiration']);

      const hasMvpd = await isMvpdUser();
      const mvpdId = (hasMvpd && (await getUserMvpdId())) || '';
      return {
        ...userInitialState,
        isAnonymousUser: true,
        hasMvpd,
        mvpdId,
        ...user,
      };
    }
  } catch (e) {
    console.log(e);
  }
  return undefined;
};

const getInitialState = (state = {}) => {
  const defaultState = getDefaultState(state);
  const appConfig = getAppConfig();
  return { ...defaultState, appConfig };
};
const {
  defaultConfig: { appEnv },
} = getAppConfig();
// NOOP in prod/staging
const wrappedDevtools =
  appEnv && ['qa', 'development'].includes(appEnv) ? devtools : (getInitialState) => getInitialState;

export const initUserState = (storeValue) => {
  const storeToUse = storeValue || store; // May not have been initialized yet

  getInitialUserState().then((user) => {
    if (user) storeToUse.setState({ user });
  });
};

// to be invoked once per app lifecycle
const configureStore = () => {
  const additionalInitialState = getInitialState(window.__INITIAL_STATE__);

  const storeValue = create(wrappedDevtools(() => ({ ...initialState, ...additionalInitialState })));

  initUserState(storeValue);

  return storeValue;
};

export let store = configureStore();

// for use in tests only
export const createMockStore = (customInitialState = {}) => {
  const additionalInitialState = getInitialState(window.__INITIAL_STATE__);

  const mockInitialState = {
    purchaseMode: {
      name: 'NEW_PLAN',
      isChangePlan: false,
      getPlansOnWelcome: true,
      showPlanDescOnPaymentReview: false,
      isPlanPayment: true,
      requirePlanSelection: true,
    },
    user: { isAuthenticated: true, email: 'test@fox.com' },
  };

  const combinedState = { ...additionalInitialState, ...mockInitialState, ...customInitialState };

  store = create(() => ({ ...initialState, ...combinedState }));

  return store;
};

if (isSSR) {
  // required for server side rendering
  window.__STORE__ = store;
}
